// Please do not use optional chaining (?.), nullish coalescing (??), or other ES2020+ features

import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Accordion, AccordionSummary, AccordionDetails, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestoreIcon from '@mui/icons-material/Restore';
import ComponentWrapper from '../../../common/ComponentWrapper';
import moment from 'moment';

export default class AgentWrapper extends ComponentWrapper {
  state = {
    messages: [],
    hasNewMessages: true,
    selectedMessageIds: [],
    isDeleting: false,
  };

  // Add debouncing for graph updates
  graphUpdateTimeout = null;

  componentDidMount() {
    if (this.props.contactId) {
      this.getUserTalks();

      this.pollingInterval = setInterval(() => {
        if (this.props.contactId) {
            this.getUserTalks();
        }
      }, 3500);
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.contactId !== this.props.contactId) {

      if (this.pollingInterval) {
        this.setState({ messages: [], hasNewMessages: true });
        clearInterval(this.pollingInterval);
      }
      
      if (this.props.contactId) {
        this.getUserTalks();

        this.pollingInterval = setInterval(() => {
            if (this.props.contactId) {
                this.getUserTalks();
            }
        }, 3500);
      }
    }

    // Use snapshot to restore scroll position after deletion
    if (snapshot && typeof snapshot.scrollTo === 'number') {
      var container = document.getElementById('agent-chat-messages-container');
      if (container) {
        if (snapshot.scrollTo === 0) {
          container.scrollTop = container.scrollHeight;
        } else {
          container.scrollTop = container.scrollHeight - snapshot.scrollTo;
        }
      }
      this._deletedMessageIds = undefined;
    }
  }

  componentWillUnmount() {
    if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
    }
    if (this.graphUpdateTimeout) {
        clearTimeout(this.graphUpdateTimeout);
    }
  }

  getSnapshotBeforeUpdate(prevProps, prevState) {
    // If we are deleting messages, capture the offsetTop of the first not-deleted message at the bottom
    if (this._deletedMessageIds && Array.isArray(this._deletedMessageIds) && this._deletedMessageIds.length > 0) {
      var messages = this.state.messages || [];
      var deletedIds = this._deletedMessageIds;
      var container = document.getElementById('agent-chat-messages-container');
      if (container && messages.length > 0) {
        // Find the first not-deleted message from the bottom
        for (var i = messages.length - 1; i >= 0; --i) {
          var msg = messages[i];
          if (deletedIds.indexOf(msg.id) === -1) {
            var el = document.getElementById('msg-' + msg.id);
            if (el) {
              // Return the offset from the bottom
              return { scrollTo: container.scrollHeight - el.offsetTop };
            }
          }
        }
        // If all bottom messages are deleted, scroll to bottom
        return { scrollTo: 0 };
      }
    }
    return null;
  }

  getUserTalks = () => {
    const { contactId } = this.props;

    if (!contactId) return;

    this.get({
      url: `/get_contacts_talks?contact_id=${contactId}&tab=web`,
      cb: (selectedContact) => {
        // Compare new messages with current messages
        const hasNewMessages = this.hasNewMessages(this.state.messages, selectedContact.msgs);
        const newMessages = selectedContact.msgs;
        
        this.setState({
            messages: newMessages,
            hasNewMessages,
        });

        // Trigger graph update if there are new messages and the last message is not from user
        if (hasNewMessages && newMessages && newMessages.length > 0) {
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage.role !== "U" && this.props.updateGraph) {
              // Debounce graph updates to prevent excessive calls
            if (this.graphUpdateTimeout) {
                clearTimeout(this.graphUpdateTimeout);
            }
            this.graphUpdateTimeout = setTimeout(() => {
                this.props.updateGraph();
            }, 500); // Wait 500ms before updating graph
          }
        }
      }
    });
  };

  // Helper method to compare messages
  hasNewMessages = (currentMessages, newMessages) => {
    if (!currentMessages || !newMessages) return true;
    if (currentMessages.length !== newMessages.length) return true;
    
    return newMessages.some((newMsg, index) => {
      const currentMsg = currentMessages[index];
      return !currentMsg || 
              newMsg.id !== currentMsg.id || 
              newMsg.formatted_body !== currentMsg.formatted_body;
    });
  };

  sendMessage = (message, cb) => {
    const { contactId } = this.props;

    if (!contactId) return;

    const data = {
      "from": `${contactId}`,
      "body": message,
      "source": 'web',
      "to": '',
      "uid": '',
    };

    this.postWithData({
      url: '/rest/conductor',
      data: data,
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      cb: (data) => {
          console.log(data);
          this.getUserTalks();
          if (cb) cb();
      },
    });
  };

  deleteSelectedMessages = (messageIdsToDelete) => {
    var self = this;
    var { selectedMessageIds } = this.state;
    var idsToDelete = messageIdsToDelete || selectedMessageIds;
    if (!idsToDelete || !idsToDelete.length) return;
    // Find the first deleted message's DOM node offsetTop
    var firstDeletedId = idsToDelete[0];
    var scrollToOffset = null;
    if (firstDeletedId && typeof window !== 'undefined') {
      var el = document.getElementById('msg-' + firstDeletedId);
      if (el) {
        scrollToOffset = el.offsetTop;
      }
    }
    this._scrollToOffsetAfterDelete = scrollToOffset;
    this._deletedMessageIds = idsToDelete.slice();
    this.setState({ isDeleting: true });
    var remaining = idsToDelete.length;
    idsToDelete.forEach(function(id) {
      self.get({
        url: `/del_msg?id=${id}`,
        cb: function() {
          remaining--;
          if (remaining === 0) {
            self.setState({ selectedMessageIds: [], isDeleting: false });
            self.getUserTalks();
          }
        }
      });
    });
  };

  toggleSelectMessage = (id) => {
    var { selectedMessageIds } = this.state;
    var idx = selectedMessageIds.indexOf(id);
    if (idx === -1) {
      this.setState({ selectedMessageIds: selectedMessageIds.concat([id]) });
    } else {
      this.setState({ selectedMessageIds: selectedMessageIds.filter(function(mid) { return mid !== id; }) });
    }
  };

  clearSelectedMessages = () => {
    this.setState({ selectedMessageIds: [] });
  };

  render() {
    return (
      <AgentChat
          sendMessage={this.sendMessage}
          messages={this.state.messages}
          hasNewMessages={this.state.hasNewMessages}
          updateGraph={this.props.updateGraph}
          selectedMessageIds={this.state.selectedMessageIds}
          toggleSelectMessage={this.toggleSelectMessage}
          deleteSelectedMessages={this.deleteSelectedMessages}
          isDeleting={this.state.isDeleting}
          clearSelectedMessages={this.clearSelectedMessages}
      />
    )
  };
};

const TypingDots = () => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          component="span"
          sx={{
            width: '4px',
            height: '4px',
            backgroundColor: 'white',
            borderRadius: '50%',
            opacity: 0.2,
            animation: 'typingDot 1.4s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes typingDot': {
                '0%': { opacity: 0.2 },
                '20%': { opacity: 1 },
                '100%': { opacity: 0.2 },
            },
          }}
        />
      ))}
    </Box>
  );
};

const parseMessageBody = (body) => {
  if (!body) return { text: '', otherData: {} };
  
  // First try to parse as JSON
  try {
    const jsonParsed = JSON.parse(body);
    if (typeof jsonParsed === 'object') {
      const { text, ...otherData } = jsonParsed;
      return {
        text: text || '',
        otherData: Object.fromEntries(
          Object.entries(otherData).filter(([key, value]) => {
            // Include all values except empty strings, null, and undefined
            if (value === null || value === undefined) return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            return true;
          })
        )
      };
    }
  } catch (e) {
    // Not a valid JSON, continue with string parsing
  }
  // If all parsing attempts fail, return the body as text
  return {
    text: body,
    otherData: {}
  };
};

// Helper function to check if a value is empty
const isEmptyValue = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
};

// Recursive component to render key-value pairs, handling objects and arrays
const RecursiveKeyValue = ({ value, level, parentKey }) => {
  if (isEmptyValue(value)) return null;
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return (
        <Box>
          {value.map((item, idx) => (
            <Box key={idx} sx={{ mb: 0.5 }}>
                <Box component="pre" sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  padding: '8px',
                  margin: '4px 0',
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  overflowX: 'auto',
                  color: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
              }}>{typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}</Box>
            </Box>
          ))}
        </Box>
      );
    } else {
      const entries = Object.entries(value).filter(([k, v]) => !isEmptyValue(v));
      if (entries.length === 0) return null;
      return (
        <Box sx={{ ml: level * 2 }}>
            {entries.map(([k, v]) => (
                <Box key={k} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 'bold', fontFamily: 'monospace', fontSize: '12px' }}>
                        {k}:
                    </Typography>
                    <Box sx={{ ml: 2 }}>
                        <RecursiveKeyValue value={v} level={level + 1} parentKey={k} />
                    </Box>
                </Box>
            ))}
        </Box>
      );
    }
  }
  // Primitive value
  return (
    <Box>
      <Box component="pre" sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: '4px',
        padding: '8px',
        margin: '4px 0',
        fontSize: '13px',
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowX: 'auto',
        color: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}>{String(value)}</Box>
    </Box>
  );
};

const MessageContent = ({ message }) => {
  const { text, otherData } = parseMessageBody(message.body);
  const nonEmptyData = Object.entries(otherData).filter(([key, value]) => !isEmptyValue(value));
  const hasOtherData = nonEmptyData.length > 0;

  const hasCyrillic = (text) => {
      return /[\u0400-\u04FF]/.test(text);
  };

  const getFontFamily = (text) => {
      if (!text) return '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      return hasCyrillic(text) 
          ? '"Roboto", "Noto Sans", "Noto Sans Cyrillic", -apple-system, BlinkMacSystemFont, sans-serif'
          : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  };

  const CodeBlock = ({ children }) => (
    <Box
      component="pre"
      sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '4px',
          padding: '8px',
          margin: '4px 0',
          fontSize: '13px',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'auto',
          color: 'rgba(255, 255, 255, 0.9)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      {children}
    </Box>
  );

  // Helper to pretty-print a JSON value if possible
  function prettyPrintIfJson(str) {
    try {
      const parsed = JSON.parse(str);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return str;
    }
  }

  const renderText = (text) => {
    if (!text) return null;
    
    const scriptResultMatch = text.match(/Script result:(.*?)(?=\n\n|$)/s);
    if (!scriptResultMatch) {
      return (
        <Box component="span" sx={{ 
            fontSize: '15px',
            color: 'white',
            display: 'block',
            fontFamily: getFontFamily(text),
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
        }}
        dangerouslySetInnerHTML={{ __html: text }}
        />
      );
    }

    const beforeScript = text.substring(0, scriptResultMatch.index);
    const scriptResult = scriptResultMatch[1].trim();
    const afterScript = text.substring(scriptResultMatch.index + scriptResultMatch[0].length);

    return (
      <React.Fragment>
        {beforeScript && (
          <Box component="span" sx={{ 
              fontSize: '15px',
              color: 'white',
              display: 'block',
              mb: 1,
              fontFamily: getFontFamily(beforeScript),
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{ __html: beforeScript }}
          />
        )}
        <Accordion 
          sx={{ 
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.MuiPaper-root.MuiAccordion-root.Mui-expanded': {
                  margin: '0',
              },
              '& .MuiAccordionSummary-root': {
                  minHeight: '24px',
                  padding: '0 4px',
              },
              '& .MuiAccordionSummary-content': {
                  margin: '4px 0',
              },
              '& .MuiAccordionDetails-root': {
                  padding: '0 4px',
              },
              '& .MuiAccordionSummary-root.Mui-expanded': {
                  minHeight: '24px',
                  padding: '0 4px',
                  margin: '0',
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                  margin: '0',
              }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
            sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '12px',
                fontFamily: getFontFamily('Script result:'),
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                }
            }}
          >
            <span>Script result:</span>
          </AccordionSummary>
          <AccordionDetails sx={{ marginTop: '3px', color: 'rgba(255, 255, 255, 0.7)' }}>
            <CodeBlock>{prettyPrintIfJson(scriptResult)}</CodeBlock>
          </AccordionDetails>
        </Accordion>
        {afterScript && (
          <Box component="span" sx={{ 
            fontSize: '15px',
            color: 'white',
            display: 'block',
            fontFamily: getFontFamily(afterScript),
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
          dangerouslySetInnerHTML={{ __html: afterScript }}
          />
        )}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {renderText(text)}
      {hasOtherData && <div style={{ marginTop: '5px', height: '1px', width: '100%', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}></div>}
      {hasOtherData && (
        <Accordion 
          sx={{ 
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:before': { display: 'none' },
              '&.MuiPaper-root.MuiAccordion-root.Mui-expanded': {
                  margin: '0',
              },
              '& .MuiAccordionSummary-root': {
                  minHeight: '24px',
                  padding: '0 4px',
              },
              '& .MuiAccordionSummary-content': {
                  margin: '4px 0',
              },
              '& .MuiAccordionDetails-root': {
                  padding: '0 4px',
              },
              '& .MuiAccordionSummary-root.Mui-expanded': {
                  minHeight: '24px',
                  padding: '0 4px',
                  margin: '0',
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                  margin: '0',
              }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
            sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '12px',
                fontFamily: getFontFamily('Additional Data'),
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                }
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span>Additional Data:</span>
                {nonEmptyData.map(([key, value], index) => (
                    <span key={index} style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{key}</span>
                ))}
            </div>
          </AccordionSummary>
          <AccordionDetails sx={{ marginTop: '3px', color: 'rgba(255, 255, 255, 0.7)' }}>
            {nonEmptyData.map(([key, value]) => {
              return (
                <Box key={key} sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontWeight: 'bold',
                      mb: 0.5,
                      fontFamily: getFontFamily(key),
                  }}>
                      {key}:
                  </Typography>
                  <RecursiveKeyValue value={value} level={1} parentKey={key} />
                </Box>
              )
            })}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

// Reusable Select Icon Component
const SelectIcon = ({ isSelected, onClick, position = 'left' }) => (
  <Box
    onClick={onClick}
    sx={{
      alignSelf: 'center',
      cursor: 'pointer',
      width: 18,
      height: 18,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isSelected ? 'rgba(23, 86, 149, 0.7)' : 'rgba(255,255,255,0.08)',
      borderRadius: '50%',
      border: isSelected ? '1.5px solid #1976d2' : '1.5px solid rgba(255,255,255,0.18)',
      transition: 'all 0.15s',
      boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
      ...(position === 'left' ? { mr: 0.5 } : { ml: 0.5 }),
    }}
  >
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="6" cy="6" r="5" stroke={isSelected ? '#1976d2' : 'rgba(255,255,255,0.3)'} strokeWidth="1.5" fill={isSelected ? '#1976d2' : 'none'} />
      {isSelected && (
        <polyline points="3.5,6.5 5.2,8.2 8.5,4.5" stroke="#fff" strokeWidth="1.5" fill="none" />
      )}
    </svg>
  </Box>
);

// Reusable Reset Icon Component
const ResetIcon = ({ onClick, position = 'left' }) => (
  <Box
    onClick={onClick}
    sx={{
      alignSelf: 'center',
      cursor: 'pointer',
      width: 18,
      height: 18,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.08)',
      borderRadius: '50%',
      border: '1.5px solid rgba(255,255,255,0.18)',
      transition: 'all 0.15s',
      boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
      '&:hover': {
        background: 'rgba(255, 193, 7, 0.2)',
        border: '1.5px solid #ffc107',
      },
      ...(position === 'left' ? { mr: 0.5 } : { ml: 0.5 }),
    }}
  >
    <RestoreIcon sx={{ color: '#ffc107', fontSize: '12px' }} />
  </Box>
);

const AgentChat = ({ messages, sendMessage, hasNewMessages, updateGraph, selectedMessageIds = [], toggleSelectMessage, deleteSelectedMessages, isDeleting, clearSelectedMessages }) => {
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetMessageId, setResetMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const userScrolledRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);

  const isNearBottom = () => {
    if (!messagesContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 500;
  };

  const scrollToBottom = (smooth = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
          behavior: smooth ? "smooth" : "auto",
          block: "end" 
      });
      setShowScrollButton(false);
      userScrolledRef.current = false;
    }
  };

  const handleScroll = () => {
    if (!isNearBottom()) {
        userScrolledRef.current = true;
    }
  };

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    // Only auto-scroll if:
    // 1. There are actually new messages
    // 2. AND (user hasn't scrolled up OR it's a new message from sending)
    if (hasNewMessages && (!userScrolledRef.current || messages.length > lastMessageCountRef.current)) {
      // Use smooth scrolling only for updates, not initial load
      scrollToBottom(!isInitialLoadRef.current);
      isInitialLoadRef.current = false;
    } else if (hasNewMessages && !isNearBottom()) {
      setShowScrollButton(true);
    }
    if (messages.length && messages[messages.length - 1].role !== "U") setIsTyping(false);
    lastMessageCountRef.current = messages.length;
  }, [messages, hasNewMessages]);

  const handleSendMessage = () => {
    const message = inputValue.trim();
    if (message) {
      setInputValue('');
      setIsTyping(true);
      userScrolledRef.current = false; // Reset scroll state when sending
      isInitialLoadRef.current = false; // Ensure smooth scroll after sending
      sendMessage(inputValue);
    }
  };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    const formatMessageTime = (timestamp) => {
      if (!timestamp) return '';
      try {
        const date = moment(timestamp);
        const now = moment();

        if (date.isSame(now, 'day')) {
            return date.format('HH:mm:ss');
        } else {
            return date.format('MMM DD, HH:mm');
        }
      } catch (e) {
        return '';
      }
    };

    const handleResetBy = (messageId) => {
      setResetMessageId(messageId);
      setShowResetDialog(true);
    };

    const handleResetConfirm = () => {
      if (resetMessageId) {
        // Find the message to get its body
        const message = messages.find(msg => msg.id === resetMessageId);
        if (message) {
          // Only set input if the message is from the user
          if (message.role === 'U') {
            const { text } = parseMessageBody(message.body);
            setInputValue(text || '');
          }
        }
        // Delete the reset message and all messages after it
        const resetIndex = messages.findIndex(msg => msg.id === resetMessageId);
        if (resetIndex !== -1) {
          const messageIdsToDelete = messages.slice(resetIndex).map(msg => msg.id);
          if (messageIdsToDelete.length > 0) {
            deleteSelectedMessages(messageIdsToDelete);
          }
        }
      }
      setShowResetDialog(false);
      setResetMessageId(null);
    };

    const handleResetCancel = () => {
      setShowResetDialog(false);
      setResetMessageId(null);
    };

    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'rgba(43, 45, 58, 1)',
        color: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Messages area */}
        <Box 
          ref={messagesContainerRef}
          id="agent-chat-messages-container"
          sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '4px',
              '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
              },
            },
          }}
        >
          {messages && messages.map((message, index) => {
              const isSelected = selectedMessageIds.indexOf(message.id) !== -1;
              const showIcons = hoveredMessageId === message.id || isSelected;
              return (
                <div
                  key={index}
                  id={'msg-' + message.id}
                  style={{
                    display: 'flex',
                    justifyContent: message.role === "U" ? 'flex-end' : 'flex-start',
                    position: 'relative',
                    flexDirection: 'row',
                  }}
                  onMouseEnter={() => setHoveredMessageId(message.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >
                  {/* For non-U messages, show the circle icon on the left */}
                  {message.role !== "U" && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px', height: '24px', width: '24px', color: 'white', backgroundColor: 'rgb(0, 0, 0)', borderRadius: '20px' }}>
                      {message.role}
                    </div>
                  )}
                  
                  {/* Icons container for U messages (left side) - always reserve space */}
                  <Box sx={{ 
                    display: message.role === "U" ? 'flex' : 'none',
                    alignItems: 'center', 
                    mr: 1.5,
                    minWidth: 36,
                    opacity: message.role === "U" && showIcons ? 1 : 0,
                    pointerEvents: message.role === "U" && showIcons ? 'auto' : 'none',
                    transition: 'opacity 0.15s ease',
                  }}>
                    <ResetIcon onClick={() => handleResetBy(message.id)} position="left" />
                    <SelectIcon 
                      isSelected={isSelected} 
                      onClick={() => toggleSelectMessage && toggleSelectMessage(message.id)} 
                      position="left" 
                    />
                  </Box>
                  
                  {/* Message bubble */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      minWidth: '250px',
                      maxWidth: '70%',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: '15px',
                        bgcolor: message.role === "U" ? '#175695' : message.role === "A" ? '#4720b8' : message.role === "T" ? '#1d7163' : '#383838',
                        borderRadius: 2,
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        opacity: isSelected ? 0.7 : 1,
                        boxShadow: isSelected ? '0 0 0 2px #1976d2' : undefined,
                        width: '100%',
                      }}
                    >
                      <MessageContent message={message} />
                      <Box sx={{ 
                        fontSize: '11px', 
                        color: 'rgba(255, 255, 255, 0.6)', 
                        mt: 0.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '-webkit-fill-available'
                      }}>
                        <span>{message.id}</span>
                        <span>{formatMessageTime(message.time)}</span>
                      </Box>
                    </Box>
                    
                    {/* Icons container for non-U messages (right side) - always reserve space */}
                    <Box sx={{ 
                      display: message.role !== "U" ? 'flex' : 'none',
                      alignItems: 'center', 
                      ml: 1.5,
                      opacity: message.role !== "U" && showIcons ? 1 : 0,
                      pointerEvents: message.role !== "U" && showIcons ? 'auto' : 'none',
                      transition: 'opacity 0.15s ease',
                    }}>
                      <ResetIcon onClick={() => handleResetBy(message.id)} position="right" />
                      <SelectIcon 
                        isSelected={isSelected} 
                        onClick={() => toggleSelectMessage && toggleSelectMessage(message.id)} 
                        position="right" 
                      />
                    </Box>
                  </Box>
                </div>
            );
        })}
        {isTyping && (
          <Box
            sx={{
                maxWidth: '100%',
                alignSelf: 'center',
                bgcolor: '#383838',
                borderRadius: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
            }}
          >
            <TypingDots />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Delete button when messages selected */}
      {selectedMessageIds && selectedMessageIds.length > 0 && (
        <Box sx={{
          position: 'absolute',
          bottom: 100,
          right: 36,
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          <IconButton
            onClick={isDeleting ? undefined : () => deleteSelectedMessages()}
            sx={{
              bgcolor: isDeleting ? 'rgba(255,255,255,0.2)' : 'rgba(25, 23, 23, 0.9)',
              color: 'white',
              borderRadius: '50%',
              width: 40,
              height: 40,
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              '&:hover': {
                bgcolor: isDeleting ? 'rgba(255,255,255,0.2)' : 'rgba(0, 0, 0, 0.8)',
              },
              fontSize: 28,
            }}
            disabled={isDeleting}
          >
            <DeleteForeverOutlinedIcon style={{ color: '#d32f2f', height: '24px', width: '24px' }}/>
          </IconButton>
          <IconButton
            onClick={clearSelectedMessages}
            sx={{
              bgcolor: 'rgba(255,255,255,0.12)',
              color: 'white',
              borderRadius: '50%',
              width: 36,
              height: 36,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.22)',
              },
              fontSize: 18,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="9" r="9" fill="#888" />
              <path d="M6 6l6 6M12 6l-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </IconButton>
        </Box>
      )}

      {/* Reset confirmation dialog */}
      <Dialog
        open={showResetDialog}
        onClose={handleResetCancel}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(43, 45, 58, 0.95)',
            color: 'white',
            borderRadius: '12px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: '300px',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: 'white', 
          fontSize: '16px',
          fontWeight: 'bold',
          pb: 1
        }}>
          Reset by this message?
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
            This will set the message content as draft and delete all messages including this one.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleResetCancel}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            sx={{
              backgroundColor: '#ffc107',
              color: '#000',
              '&:hover': {
                backgroundColor: '#ffb300',
              },
            }}
          >
            Yes, reset by
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <Box
          onClick={scrollToBottom}
          sx={{
            position: 'absolute',
            bottom: '100px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '50%',
            width: '30px',
            height: '30px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                transform: 'translateX(-50%) scale(1.1)',
            },
            zIndex: 1000,
          }}
        >
            <KeyboardArrowDownIcon sx={{ color: 'white' }} />
        </Box>
      )}

      {/* Input area */}
      <Box sx={{
        p: 2,
        display: 'flex',
        gap: 1,
        bgcolor: 'rgba(52, 53, 65, 0.7)',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(82, 82, 82, 0.3)',
        mx: 2,
        mb: 2,
        position: 'relative',
      }}>
        <TextField
          fullWidth
          variant="standard"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Hey, I'm here to help you with your planning..."
          multiline
          maxRows={4}
          sx={{
            fontSize: '14px',
            '& .MuiInput-root': {
              color: 'white',
              '&:before, &:after': {
                  display: 'none',
              },
            },
            '& .MuiInput-input': {
              p: 1,
              '&::placeholder': {
                  fontWeight: 300,
                  color: 'rgba(255, 255, 255, 0.5)',
                  opacity: 1,
              },
            },
          }}
        />
        <IconButton 
          onClick={() => handleSendMessage()}
          sx={{
            height: '42px',
            width: '42px',
            '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <SendIcon sx={{ color: 'rgba(255, 255, 255, 0.86)' }} />
        </IconButton>
      </Box>
    </Box>
  );
};
